<template>
  <div
    v-if="(hoverRow && !hoverCol) && (selectedWidgetState === null) && !isDragging"
    class="sb-title"
  >
    <span>{{ t('statsEditor.row') }}</span>
  </div>
  <div
    class="grid stats-builder-row"
    :class="{ 'rowInHover': hoverRow && !hoverCol && (selectedWidgetState === null) && !isDragging }"
    @mouseover="(e) => mouseoverRow(e)"
    @mouseleave="(e) => mouseleaveRow(e)"
  >
    <div
      v-if="!isDragging"
      class="sb-row-actions"
      :class="{ 'is-visible': (hoverRow && !hoverCol) && (selectedWidgetState === null) }"
      @mouseover="(e) => mouseoverRow(e)"
      @mouseleave="(e) => mouseleaveRow(e)"
    >
      <div class="flex flex-column">
        <div
          v-tooltip.right="t('statsEditor.actions.tooltip.row.move')"
          class="flex sb-action handle mb-1"
          icon="far fa-arrows"
        >
          <i class="far fa-arrows" />
        </div>
        <div
          v-tooltip.right="t('statsEditor.actions.tooltip.row.duplicate')"
          class="flex sb-action mb-1"
          @click="duplicateRow"
        >
          <i class="far fa-clone" />
        </div>
        <div
          v-tooltip.right="t('statsEditor.actions.tooltip.row.remove')"
          class="flex sb-action mb-1"
          @click="deleteRow"
        >
          <i class="far fa-trash" />
        </div>
      </div>
    </div>
    <StatsBuilderSubCol
      v-for="(columnData, ii) in rowData.children"
      :key="columnData.id"
      :row-parent-id="rowParentId"
      :column-parent-id="columnParentId"
      :panel-id="panelId"
      :row-id="rowData.id"
      :column-data="columnData"
      :row-has-column-with-multiple-widgets="rowHasColumnWithMultipleWidgets"
      @mouseover-col="mouseoverRow"
      @mouseleave-col="mouseleaveRow"
    />
  </div>
  <ConfirmDialog :group="`removeRow_${panelId}_${rowData.id}`" />
</template>

<script lang="ts">
import {
  computed, defineComponent,
  Ref,
  ref, SetupContext,
  watch,
} from 'vue';
import { store } from '@/store';
import Tooltip from 'primevue/tooltip';
import { useI18n } from 'vue-i18n';
import { StatsBuilderSelectedWidget } from '@/types/stats-editor-types';
import StatsBuilderSubCol from '@/components/stats-builder/StatsBuilderSubCol.vue';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmDialog from 'primevue/confirmdialog';

export default defineComponent({
  name: 'StatsBuilderSubRow',

  components: {
    StatsBuilderSubCol,
    ConfirmDialog,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    rowParentId: {
      type: String,
      required: true,
    },

    columnParentId: {
      type: String,
      required: true,
    },

    panelId: {
      type: String,
      required: true,
    },

    rowData: {
      type: Object,
      required: false,
    },
  },

  setup(props: { rowParentId: string; columnParentId: string; panelId: string; rowData: any }, { emit }: SetupContext) {
    const { t } = useI18n();
    const confirm = useConfirm();

    const selectedWidgetState: Ref<StatsBuilderSelectedWidget|null> = computed(() => store.getters['statsEditor/getSelectedWidget']);
    const isDragging = computed(() => store.getters['statsEditor/getIsDraggingBuilderItem']);

    const rowHasColumnWithMultipleWidgets = computed(() => {
      if (props.rowData.children) {
        return props.rowData.children.some((column: any) => {
          if (column.widgets && column.widgets.length > 1) {
            return true;
          }
          return false;
        });
      }
      return false;
    });

    const timeoutId = ref<any>(null);

    const hoverRow = ref(false);
    const hoverCol = ref(false);
    const mouseoverRow = (e: any) => {
      hoverRow.value = true;
      if (timeoutId.value) {
        clearTimeout(timeoutId.value);
      }
    };
    const mouseleaveRow = (e: any) => {
      timeoutId.value = setTimeout(() => {
        hoverRow.value = false;
      }, 200);
    };

    watch(isDragging, (dragging) => {
      if (dragging) {
        hoverRow.value = false;
      }
    });

    const deleteRow = () => {
      confirm.require({
        message: t('statsEditor.confirmationMessages.removeRow'),
        header: 'Confirmation',
        group: `removeRow_${props.panelId}_${props.rowData.id}`,
        icon: 'far fa-exclamation-triangle',
        acceptLabel: t('yes'),
        rejectLabel: t('no'),
        accept: () => {
          store.dispatch('statsEditor/removeRow', {
            location: {
              rowId: props.rowParentId,
              columnId: props.columnParentId,
              panelData: {
                panelId: props.panelId,
                subRowId: props.rowData.id,
              },
            },
          });
        },
      });
    };

    const duplicateRow = () => {
      confirm.require({
        message: t('statsEditor.confirmationMessages.duplicateRow'),
        header: 'Confirmation',
        group: `removeRow_${props.panelId}_${props.rowData.id}`,
        icon: 'far fa-exclamation-triangle',
        acceptLabel: t('yes'),
        rejectLabel: t('no'),
        accept: () => {
          store.dispatch('statsEditor/duplicateRow', {
            location: {
              rowId: props.rowParentId,
              columnId: props.columnParentId,
              panelData: {
                panelId: props.panelId,
                subRowId: props.rowData.id,
              },
            },
          });
        },
      });
    };

    return {
      t,
      selectedWidgetState,
      rowHasColumnWithMultipleWidgets,
      hoverRow,
      hoverCol,

      mouseoverRow,
      mouseleaveRow,
      deleteRow,
      duplicateRow,
      isDragging,
    };
  },
});
</script>

<style scoped lang="scss">
  .sb-title {
    position: absolute;
    background-color: #495057;
    z-index: 2;
    line-height: 1rem;
    font-size: 0.8rem;
    color: white;
    top: -1.5rem;
    left: 0;
    padding: 6px 10px 3px 8px;
    border-top-right-radius: 3px;
    border-top-left-radius: 3px;
    justify-content: center;
    display: flex;
    align-items: center;
  }

  .stats-builder-row {
    position: relative;
    border-radius: 0.5rem;
    border: 1px solid #dee2e6;
    padding: 0.75rem 1rem 0.75rem 2.9rem;
    background-color: #ffffff;

    &.rowInHover {
      border: 2px solid #495057;
      border-top-left-radius: 0;
    }
  }

  .sb-row-actions {
    position: absolute;
    top: 0.85rem;
    left: 0.95rem;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.18s ease;

    &.is-visible {
      opacity: 1;
      pointer-events: auto;
    }
  }

  .sb-action {
    cursor: pointer;
    padding: 0.55rem;
    background: #ffffff;
    border: solid 1px #ced4da;
    border-radius: 0.6rem;
    box-shadow: 0 12px 24px -20px rgba(73, 80, 87, 0.35);
    transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

    .handle {
      cursor: grab;
    }
  }

  .sb-action:hover {
    background: #ffffff;
    color: #495057;
    border-color: rgba(73, 80, 87, 0.4);
    box-shadow: 0 16px 30px -22px rgba(73, 80, 87, 0.4);
    transform: translateY(-1px);
  }
</style>
